import { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useRouter } from '@tanstack/react-router';
import type { UseFormReturn } from 'react-hook-form';
import { ClipboardList } from 'lucide-react';
import { BackButton } from '@/components/common/BackButton';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { FormSkeleton } from '@/components/common/FormSkeleton';
import { CenteredContentLayout } from '@/components/layout/CenteredContentLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { SurveyForm, type SurveyFormHandle } from '../components/SurveyForm/SurveyForm';
import { SurveyFormActions } from '../components/SurveyForm/SurveyFormActions';
import { surveyDetailQueryOptions } from '../api/surveys.api';
import { useUpdateSurvey } from '../hooks/useUpdateSurvey';
import type { CreateSurveyFormValues } from '../types/survey.types';
import { toSurveyFormDefaultValues, toUpdateSurveyPayload } from '../utils/surveyFormMappers';

export function SurveyEditPage() {
  const { surveyId } = useParams({ from: '/_auth/feedback/surveys/$surveyId/edit' });
  const router = useRouter();
  const formRef = useRef<SurveyFormHandle>(null);
  const submittedValuesRef = useRef<CreateSurveyFormValues | null>(null);

  const {
    data: survey,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery(surveyDetailQueryOptions(surveyId));

  const defaultValues = useMemo(
    () => (survey ? toSurveyFormDefaultValues(survey) : undefined),
    [survey],
  );

  function goToDetail() {
    void router.navigate({
      to: '/feedback/surveys/$surveyId',
      params: { surveyId },
    });
  }

  const { mutate: updateSurveyMutation, isPending } = useUpdateSurvey(surveyId, () => {
    if (submittedValuesRef.current) {
      formRef.current?.reset(submittedValuesRef.current);
      submittedValuesRef.current = null;
    }
    goToDetail();
  });

  function handleSubmit(
    values: CreateSurveyFormValues,
    form: UseFormReturn<CreateSurveyFormValues>,
  ) {
    submittedValuesRef.current = form.getValues();
    updateSurveyMutation(toUpdateSurveyPayload(values));
  }

  function renderContent() {
    if (isLoading) return <FormSkeleton />;
    if (error) {
      return (
        <ErrorState
          error={error}
          fallbackTitle="Could not load this survey"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      );
    }
    if (!survey || !defaultValues) {
      return (
        <EmptyState
          icon={ClipboardList}
          title="Survey unavailable"
          description="This survey may have been deleted or the link is no longer valid."
          fill
        />
      );
    }
    return (
      <SurveyForm
        ref={formRef}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onCancel={goToDetail}
        isPending={isPending}
      />
    );
  }

  const isFormShown = !isLoading && !error && defaultValues !== undefined;

  return (
    <CenteredContentLayout
      header={
        <PageHeader
          title="Edit Survey"
          leading={<BackButton onClick={goToDetail} />}
          actions={
            isFormShown ? (
              <SurveyFormActions
                submitLabel="Update Survey"
                pendingLabel="Updating..."
                isPending={isPending}
                onCancel={() => formRef.current?.requestCancel()}
              />
            ) : undefined
          }
        />
      }
    >
      {renderContent()}
    </CenteredContentLayout>
  );
}
